<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import { cn } from "../cn";

    export let open = false;
    export let title: string = "";
    export let description: string = "";
    export let size: "sm" | "md" | "lg" | "xl" = "md";

    // Если задан storageKey — диалог становится ресайзируемым: drag за
    // правый-нижний угол, размеры запоминаются в localStorage.
    export let resizable = false;
    export let storageKey: string = "";
    export let defaultWidth = 640;
    export let defaultHeight = 520;
    export let minWidth = 360;
    export let minHeight = 240;

    let className = "";
    export { className as class };

    const sizes: Record<string, string> = {
        sm: "max-w-sm",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
    };

    let dialogEl: HTMLDivElement;
    let width = defaultWidth;
    let height = defaultHeight;

    function loadSize() {
        if (!resizable || !storageKey || typeof localStorage === "undefined")
            return;
        try {
            const raw = localStorage.getItem("piq.dialog." + storageKey);
            if (!raw) return;
            const v = JSON.parse(raw);
            if (typeof v.width === "number") width = v.width;
            if (typeof v.height === "number") height = v.height;
        } catch {}
    }
    function saveSize() {
        if (!resizable || !storageKey || typeof localStorage === "undefined")
            return;
        try {
            localStorage.setItem(
                "piq.dialog." + storageKey,
                JSON.stringify({ width, height }),
            );
        } catch {}
    }

    function close() {
        open = false;
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === "Escape" && open) close();
    }

    function startResize(e: MouseEvent) {
        if (!resizable || !dialogEl) return;
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startW = width;
        const startH = height;

        // Отключаем transitions на время — иначе тащит лагово.
        dialogEl.style.transition = "none";

        function onMove(ev: MouseEvent) {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;
            // Diagonal × 2 — потому что диалог центрирован и растёт в обе стороны.
            const w = Math.min(
                Math.max(minWidth, startW + dx * 2),
                window.innerWidth - 32,
            );
            const h = Math.min(
                Math.max(minHeight, startH + dy * 2),
                window.innerHeight - 32,
            );
            dialogEl.style.width = w + "px";
            dialogEl.style.height = h + "px";
            width = w;
            height = h;
        }
        function onUp() {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            if (dialogEl) dialogEl.style.transition = "";
            saveSize();
        }
        document.body.style.cursor = "se-resize";
        document.body.style.userSelect = "none";
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    }

    onMount(() => {
        loadSize();
        document.addEventListener("keydown", handleKeydown);
    });
    onDestroy(() => {
        document.removeEventListener("keydown", handleKeydown);
    });

    $: if (typeof document !== "undefined") {
        document.body.style.overflow = open ? "hidden" : "";
    }

    $: contentStyle = resizable
        ? `width: ${width}px; max-width: calc(100vw - 2rem); height: ${height}px; max-height: calc(100vh - 2rem);`
        : "";
</script>

{#if open}
    <div
        class="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-150"
        on:click={close}
        role="presentation"
    ></div>

    {#if resizable}
        <div
            bind:this={dialogEl}
            class={cn(
                "fixed top-1/2 left-1/2 z-[101] -translate-x-1/2 -translate-y-1/2 bg-[var(--card)] text-[var(--card-foreground)] rounded-2xl border border-[var(--border)]/50 shadow-2xl shadow-black/30 flex flex-col overflow-hidden animate-in zoom-in-95 fade-in-0 duration-150",
                className,
            )}
            style={contentStyle}
            role="dialog"
            aria-modal="true"
        >
            {#if title || description}
                <div class="flex flex-col gap-1.5 px-6 pt-6 pb-4 pr-12 shrink-0">
                    {#if title}
                        <div class="text-lg leading-none font-semibold">
                            {title}
                        </div>
                    {/if}
                    {#if description}
                        <div class="text-sm text-[var(--muted-foreground)]">
                            {description}
                        </div>
                    {/if}
                </div>
            {/if}

            <button
                type="button"
                on:click={close}
                aria-label="Close"
                class="absolute top-4 right-4 size-7 rounded-md inline-flex items-center justify-center text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] transition-colors cursor-pointer z-10"
            >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <path d="M3 3l10 10M13 3L3 13" />
                </svg>
            </button>

            <div class="flex-1 overflow-y-auto px-6 pb-6">
                <slot />
            </div>

            <div
                on:mousedown={startResize}
                class="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize z-20 flex items-end justify-end p-0.5 opacity-40 hover:opacity-100 transition-opacity"
                title="Потяните для изменения размера"
                role="button"
                aria-label="Resize"
            >
                <svg width="10" height="10" viewBox="0 0 10 10" class="text-[var(--muted-foreground)]">
                    <path d="M9 1L1 9M9 4L4 9M9 7L7 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                </svg>
            </div>
        </div>
    {:else}
        <div
            class={cn(
                "fixed top-1/2 left-1/2 z-[101] w-full -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto px-4",
                sizes[size],
            )}
        >
            <div
                class={cn(
                    "relative bg-[var(--card)] text-[var(--card-foreground)] rounded-2xl border border-[var(--border)]/50 shadow-2xl shadow-black/30 p-6 animate-in zoom-in-95 fade-in-0 duration-150",
                    className,
                )}
                role="dialog"
                aria-modal="true"
            >
                {#if title || description}
                    <div class="flex flex-col gap-1.5 mb-5 pr-8">
                        {#if title}
                            <div class="text-lg leading-none font-semibold">
                                {title}
                            </div>
                        {/if}
                        {#if description}
                            <div class="text-sm text-[var(--muted-foreground)]">
                                {description}
                            </div>
                        {/if}
                    </div>
                {/if}

                <button
                    type="button"
                    on:click={close}
                    aria-label="Close"
                    class="absolute top-4 right-4 size-7 rounded-md inline-flex items-center justify-center text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] transition-colors cursor-pointer"
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <path d="M3 3l10 10M13 3L3 13" />
                    </svg>
                </button>

                <slot />
            </div>
        </div>
    {/if}
{/if}
